// src/components/team/ValuationRequestsSection.jsx
import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabaseClient";
import { TrendingUp, Clock, CheckCircle, Mail, User, AlertCircle } from "lucide-react";

export default function ValuationRequestsSection() {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [experts, setExperts] = useState([]);
  const [filter, setFilter] = useState("all"); // all, pending, assigned, completed

  useEffect(() => {
    loadRequests();
    loadExperts();
  }, []);

  async function loadRequests() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("partner_requests")
        .select(`
          *,
          client:client_id(id, email, first_name, last_name)
        `)
        .eq("type", "valuation")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (err) {
      console.error("Erreur chargement demandes de valorisation:", err);
    } finally {
      setLoading(false);
    }
  }

  async function loadExperts() {
    try {
      const { data, error } = await supabase
        .from("experts")
        .select("id, name, specialty, expert_user_id, contact_email")
        .eq("is_public", true)
        .order("name");

      if (error) throw error;
      setExperts(data || []);
    } catch (err) {
      console.error("Erreur chargement experts:", err);
    }
  }

  async function assignExpert(requestId, expertId) {
    try {
      const expert = experts.find((e) => e.id === expertId);
      const request = requests.find((r) => r.id === requestId);
      const nextPayload = {
        ...(request?.payload || {}),
        assignedExpertId: expertId,
        assignedExpertName: expert?.name || null,
        assignedExpertHasAccount: !!expert?.expert_user_id,
      };

      const { error } = await supabase
        .from("partner_requests")
        .update({
          status: "assigned",
          expert_id: expert?.id || null,
          payload: nextPayload,
        })
        .eq("id", requestId);

      if (error) throw error;
      await loadRequests();
    } catch (err) {
      console.error("Erreur assignation expert:", err);
      alert("Erreur lors de l'assignation");
    }
  }

  async function updateStatus(requestId, newStatus) {
    try {
      const { error } = await supabase
        .from("partner_requests")
        .update({ status: newStatus })
        .eq("id", requestId);

      if (error) throw error;
      await loadRequests();
    } catch (err) {
      console.error("Erreur mise à jour statut:", err);
      alert("Erreur lors de la mise à jour");
    }
  }

  const filteredRequests = requests.filter((req) => {
    if (filter === "all") return true;
    if (filter === "pending") return req.status === "pending";
    if (filter === "assigned") return req.status === "assigned" || req.status === "in_progress";
    if (filter === "completed") return req.status === "completed";
    return true;
  });

  const stats = {
    total: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    assigned: requests.filter((r) => r.status === "assigned" || r.status === "in_progress").length,
    completed: requests.filter((r) => r.status === "completed").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "Georgia, serif", backgroundColor: "#fafaf9" }}>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-6 h-6" style={{ color: "#1a1a1a" }} />
          <h2 className="text-2xl font-semibold" style={{ color: "#1a1a1a" }}>
            Demandes d'estimation par un expert
          </h2>
        </div>
        <p className="text-sm" style={{ color: "#6b7280" }}>
          Demandes de valorisation d'une œuvre adressées à un expert partenaire nommé
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="p-4 rounded-lg" style={{ backgroundColor: "white", border: "1px solid #e5e5e5" }}>
          <div className="text-sm mb-1" style={{ color: "#6b7280" }}>Total</div>
          <div className="text-2xl font-bold" style={{ color: "#1a1a1a" }}>{stats.total}</div>
        </div>
        <div className="p-4 rounded-lg" style={{ backgroundColor: "white", border: "1px solid #e5e5e5" }}>
          <div className="text-sm mb-1" style={{ color: "#6b7280" }}>En attente</div>
          <div className="text-2xl font-bold" style={{ color: "#f59e0b" }}>{stats.pending}</div>
        </div>
        <div className="p-4 rounded-lg" style={{ backgroundColor: "white", border: "1px solid #e5e5e5" }}>
          <div className="text-sm mb-1" style={{ color: "#6b7280" }}>Assignées</div>
          <div className="text-2xl font-bold" style={{ color: "#3b82f6" }}>{stats.assigned}</div>
        </div>
        <div className="p-4 rounded-lg" style={{ backgroundColor: "white", border: "1px solid #e5e5e5" }}>
          <div className="text-sm mb-1" style={{ color: "#6b7280" }}>Terminées</div>
          <div className="text-2xl font-bold" style={{ color: "#10b981" }}>{stats.completed}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {["all", "pending", "assigned", "completed"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-md text-sm ${
              filter === f ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            {f === "all" ? "Toutes" : f === "pending" ? "En attente" : f === "assigned" ? "Assignées" : "Terminées"}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12 rounded-lg" style={{ backgroundColor: "white", border: "1px solid #e5e5e5" }}>
            <TrendingUp className="w-12 h-12 mx-auto mb-3" style={{ color: "#d1d5db" }} />
            <p style={{ color: "#6b7280" }}>Aucune demande dans cette catégorie</p>
          </div>
        ) : (
          filteredRequests.map((request) => {
            const payload = request.payload || {};
            const client = request.client || {};
            const expertChoice = payload.expertChoice || "artkiv_selects";
            const selectedExpertName = payload.selectedExpertName;
            const selectedExpertHasAccount = payload.selectedExpertHasAccount;
            const assignedExpertName = payload.assignedExpertName;

            return (
              <div key={request.id} className="p-5 rounded-lg" style={{ backgroundColor: "white", border: "1px solid #e5e5e5" }}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold" style={{ color: "#1a1a1a" }}>
                        {payload.artwork_title
                          ? `Estimation — ${payload.artwork_title}${payload.artwork_artist ? ` (${payload.artwork_artist})` : ""}`
                          : `Estimation — Œuvre ${request.artwork_id || payload.artwork_id || ""}`}
                      </h3>
                      <span
                        className="px-2 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor:
                            request.status === "pending" ? "#fef3c7"
                            : request.status === "assigned" || request.status === "in_progress" ? "#dbeafe"
                            : "#d1fae5",
                          color:
                            request.status === "pending" ? "#92400e"
                            : request.status === "assigned" || request.status === "in_progress" ? "#1e40af"
                            : "#065f46",
                        }}
                      >
                        {request.status === "pending" ? "En attente"
                          : request.status === "assigned" ? "Assignée"
                          : request.status === "in_progress" ? "En cours"
                          : "Terminée"}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm" style={{ color: "#6b7280" }}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>Client: {client.first_name} {client.last_name} ({client.email})</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Demandé le {new Date(request.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>

                      <div className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium mb-1">Objectif: {payload.purpose || payload.context}</div>
                          {payload.details && (
                            <div className="text-xs" style={{ color: "#9ca3af" }}>{payload.details}</div>
                          )}
                        </div>
                      </div>

                      {expertChoice === "i_choose" && selectedExpertName && (
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          <span>
                            Expert demandé par le client: {selectedExpertName}
                            {!selectedExpertHasAccount && (
                              <strong style={{ color: "#c9a961" }}> — sans compte Artkiv, à contacter manuellement par email</strong>
                            )}
                          </span>
                        </div>
                      )}

                      {assignedExpertName && (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Expert assigné: {assignedExpertName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {request.status === "pending" && (
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <select
                      onChange={(e) => {
                        if (e.target.value) assignExpert(request.id, e.target.value);
                      }}
                      className="flex-1 border rounded-md px-3 py-2 text-sm"
                      style={{ backgroundColor: "white", borderColor: "#d4d4d4" }}
                      defaultValue=""
                    >
                      <option value="">Assigner à un expert...</option>
                      {experts.map((expert) => (
                        <option key={expert.id} value={expert.id}>
                          {expert.name} - {expert.specialty}{!expert.expert_user_id ? " (sans compte)" : ""}
                        </option>
                      ))}
                    </select>

                    <a
                      href={`mailto:${client.email}?subject=Votre demande d'estimation Artkiv`}
                      className="px-4 py-2 rounded-md text-sm flex items-center gap-2"
                      style={{ backgroundColor: "white", border: "1px solid #d4d4d4", color: "#1a1a1a" }}
                    >
                      <Mail className="w-4 h-4" />
                      Contacter
                    </a>
                  </div>
                )}

                {(request.status === "assigned" || request.status === "in_progress") && (
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => updateStatus(request.id, "completed")}
                      className="px-4 py-2 rounded-md text-sm"
                      style={{ backgroundColor: "#10b981", color: "white" }}
                    >
                      Marquer comme terminée
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
